import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {observer} from "mobx-react-lite";
import {Context} from "..";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import {fetchServerLogsAPI, indexServers, sendRcon} from "../http/ServerAPI";
import {getApiErrorMessage} from "../http/apiError";
import {useSnackbar} from "../components/snackbar/SnackbarProvider";
import {SERVERS_ROUTE} from "../utils/consts";

const decodeHtmlEntities = (value) => {
    if (typeof value !== 'string') {
        return value;
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
};

const normalizeLogResponse = (response, serverId) => {
    const payload = response?.message ? response : response?.data || response || {};
    const logs = Array.isArray(payload)
        ? payload
        : payload?.logs || payload?.data || [];

    return {
        log: Number(payload?.server_id || serverId),
        data: Array.isArray(logs)
            ? logs.map((item) => ({
                ...item,
                message: decodeHtmlEntities(item?.message || ''),
            }))
            : [],
    };
};

const formatLogDate = (date) => {
    if (!date) {
        return '';
    }

    const normalized = typeof date === 'string'
        ? date.replace(' ', 'T')
        : date;

    const parsed = new Date(normalized);

    if (Number.isNaN(parsed.getTime())) {
        return String(date);
    }

    return parsed.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
};

const getServerTitle = (servers, serverId) => {
    const server = (servers || []).find((item) => Number(item.server_id || item.id) === Number(serverId));

    return server?.title || server?.name || server?.server_name || `Сервер #${serverId}`;
};

const Log = observer(() => {
    const {server_id} = useParams();
    const numericServerId = useMemo(() => Number(server_id), [server_id]);
    const navigate = useNavigate();

    const {user} = useContext(Context);
    const snackbar = useSnackbar();

    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [isLoadingServerName, setIsLoadingServerName] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [message, setMessage] = useState("");
    const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
    const [unseenCount, setUnseenCount] = useState(0);

    const isPaused = user.isLogPaused(numericServerId);
    const serverTitle = useMemo(() => getServerTitle(user.servers, numericServerId), [user.servers, numericServerId]);

    const logContainerRef = useRef(null);
    const prevLogCountRef = useRef(0);
    const isPinnedToBottomRef = useRef(true);

    const isNearBottom = useCallback(() => {
        const container = logContainerRef.current;

        if (!container) {
            return true;
        }

        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        return distanceFromBottom < 48;
    }, []);

    const setPinned = useCallback((value) => {
        isPinnedToBottomRef.current = value;
        setIsPinnedToBottom(value);
    }, []);

    const scrollLogsToBottom = useCallback((behavior = 'auto') => {
        const container = logContainerRef.current;

        if (!container) {
            return;
        }

        container.scrollTo({
            top: container.scrollHeight,
            behavior,
        });

        setPinned(true);
        setUnseenCount(0);
    }, [setPinned]);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate(SERVERS_ROUTE);
    };

    const loadInitialLogs = useCallback(async ({silent = false} = {}) => {
        if (!numericServerId) {
            return;
        }

        user.clearLog(numericServerId);
        setChatMessages([]);
        prevLogCountRef.current = 0;
        setUnseenCount(0);
        setPinned(true);

        if (!silent) {
            setIsLoadingLogs(true);
        }

        try {
            const response = await fetchServerLogsAPI(numericServerId);
            const storage = normalizeLogResponse(response, numericServerId);

            user.replaceLog(storage);
            setChatMessages(storage.data);
            prevLogCountRef.current = storage.data.length;

            requestAnimationFrame(() => scrollLogsToBottom('auto'));

            if (!silent) {
                snackbar.success('История логов загружена');
            }
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, 'Не удалось загрузить историю логов'));
        } finally {
            if (!silent) {
                setIsLoadingLogs(false);
            }
        }
    }, [numericServerId, scrollLogsToBottom, setPinned, snackbar, user]);

    useEffect(() => {
        if (user.userName) {
            setName(user.userName);
            setRole(user.role);
        } else {
            setName('');
            setRole('');
        }
    }, [user.userName, user.role]);

    useEffect(() => {
        const hasServerName = (user.servers || []).some((item) => Number(item.server_id || item.id) === numericServerId);

        if (!numericServerId || hasServerName || isLoadingServerName) {
            return;
        }

        let isMounted = true;

        const loadServers = async () => {
            setIsLoadingServerName(true);

            try {
                const data = await indexServers();

                if (isMounted) {
                    user.setServers(data);
                }
            } catch (e) {
                // Не мешаем логам работать, если название сервера получить не удалось.
                console.warn('[log] Не удалось загрузить список серверов для заголовка', e);
            } finally {
                if (isMounted) {
                    setIsLoadingServerName(false);
                }
            }
        };

        loadServers();

        return () => {
            isMounted = false;
        };
    }, [isLoadingServerName, numericServerId, user, user.servers]);

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            if (!isMounted) {
                return;
            }

            await loadInitialLogs({silent: true});

            if (isMounted) {
                setIsLoadingLogs(false);
            }
        };

        if (numericServerId) {
            setIsLoadingLogs(true);
            user.resumeLog(numericServerId);
            load();
        }

        return () => {
            isMounted = false;
        };
    }, [numericServerId, loadInitialLogs, user]);

    useEffect(() => {
        if (isPaused) {
            return;
        }

        const logData = user.getStorageByLogId(numericServerId);
        const nextMessages = logData.data || [];
        const prevCount = prevLogCountRef.current;
        const nextCount = nextMessages.length;
        const addedCount = Math.max(nextCount - prevCount, 0);
        const shouldStickToBottom = isPinnedToBottomRef.current || isNearBottom();

        setChatMessages(nextMessages);
        prevLogCountRef.current = nextCount;

        if (addedCount > 0 && !shouldStickToBottom) {
            setUnseenCount((current) => current + addedCount);
            setPinned(false);
            return;
        }

        if (shouldStickToBottom) {
            requestAnimationFrame(() => scrollLogsToBottom('smooth'));
        }
    }, [user.log, numericServerId, user, isPaused, isNearBottom, scrollLogsToBottom, setPinned]);

    const handleLogScroll = () => {
        const nearBottom = isNearBottom();
        setPinned(nearBottom);

        if (nearBottom) {
            setUnseenCount(0);
        }
    };

    const handlePause = () => {
        user.pauseLog(numericServerId);
        snackbar.warning('Приём новых WSS-логов приостановлен');
    };

    const handleResume = async () => {
        user.resumeLog(numericServerId);
        await loadInitialLogs({silent: false});
        snackbar.success('Приём WSS-логов возобновлён');
    };

    const handleRefresh = async () => {
        await loadInitialLogs({silent: false});
    };

    const sendMessage = async (serverId, command) => {
        try {
            await sendRcon(serverId, command, 'command');
            snackbar.success('Команда отправлена');
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, 'Не удалось отправить команду'));
        }
    };

    const handleSendMessage = () => {
        const command = message.trim();

        if (command) {
            sendMessage(numericServerId, command);
            setMessage("");
        }
    };

    return (
        <Box
            sx={{
                height: '100dvh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#0b0f14',
                color: '#d8dee9',
            }}
        >
            <Paper
                elevation={0}
                square
                sx={{
                    px: {xs: 0.75, sm: 1.5},
                    py: {xs: 0.65, sm: 0.9},
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    background: 'linear-gradient(90deg, rgba(17,24,31,0.98), rgba(11,15,20,0.98))',
                    flexShrink: 0,
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{minWidth: 0}}
                >
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{minWidth: 0, flex: 1}}>
                        <Tooltip title="Назад к серверам">
                            <IconButton
                                size="small"
                                onClick={handleBack}
                                sx={{
                                    color: '#d8dee9',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    flexShrink: 0,
                                }}
                            >
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Box sx={{minWidth: 0}}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 0}}>
                                <Box
                                    sx={{
                                        width: 9,
                                        height: 9,
                                        borderRadius: '50%',
                                        bgcolor: isPaused ? 'warning.main' : 'success.main',
                                        boxShadow: isPaused
                                            ? '0 0 12px rgba(255,193,7,0.8)'
                                            : '0 0 12px rgba(0,197,138,0.8)',
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography
                                    variant="subtitle1"
                                    noWrap
                                    sx={{fontWeight: 800, letterSpacing: 0.2, lineHeight: 1.2}}
                                >
                                    {serverTitle}
                                </Typography>
                            </Stack>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{display: {xs: 'none', sm: 'block'}}}
                            >
                                {name ? `${name}${role ? ` · ${role}` : ''}` : 'Realtime console'} · logs
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{flexShrink: 0}}>
                        <Chip
                            size="small"
                            label={isPaused ? 'Пауза' : 'Live'}
                            color={isPaused ? 'warning' : 'success'}
                            variant={isPaused ? 'filled' : 'outlined'}
                            sx={{height: 24}}
                        />
                        <Chip
                            size="small"
                            label={chatMessages.length}
                            variant="outlined"
                            sx={{display: {xs: 'none', sm: 'inline-flex'}, height: 24}}
                        />
                        {isLoadingLogs && <CircularProgress size={18} />}
                    </Stack>
                </Stack>

                <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{mt: 0.75, overflowX: 'auto', pb: 0.25}}
                >
                    {isPaused ? (
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<PlayArrowIcon />}
                            onClick={handleResume}
                            disabled={isLoadingLogs}
                            sx={{whiteSpace: 'nowrap', minWidth: 'auto'}}
                        >
                            Старт
                        </Button>
                    ) : (
                        <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            startIcon={<PauseIcon />}
                            onClick={handlePause}
                            sx={{whiteSpace: 'nowrap', minWidth: 'auto'}}
                        >
                            Пауза
                        </Button>
                    )}

                    <Tooltip title="Очистить текущий список и заново загрузить последние записи через API">
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={handleRefresh}
                                disabled={isLoadingLogs}
                                sx={{whiteSpace: 'nowrap', minWidth: 'auto'}}
                            >
                                Обновить
                            </Button>
                        </span>
                    </Tooltip>

                    <Button
                        size="small"
                        variant={isPinnedToBottom ? 'text' : 'outlined'}
                        startIcon={<KeyboardArrowDownIcon />}
                        onClick={() => scrollLogsToBottom('smooth')}
                        sx={{whiteSpace: 'nowrap', minWidth: 'auto'}}
                    >
                        Вниз
                    </Button>
                </Stack>
            </Paper>

            {isPaused && (
                <Box
                    sx={{
                        px: {xs: 1.25, sm: 2},
                        py: 0.65,
                        borderBottom: '1px solid rgba(255,193,7,0.18)',
                        backgroundColor: 'rgba(255,193,7,0.08)',
                        flexShrink: 0,
                    }}
                >
                    <Typography variant="caption" color="warning.main">
                        Пауза: WSS-обновления временно не попадают в вывод. «Старт» заново загрузит историю и вернёт live-режим.
                    </Typography>
                </Box>
            )}

            <Box sx={{position: 'relative', flex: 1, minHeight: 0, bgcolor: '#0b0f14'}}>
                <Box
                    ref={logContainerRef}
                    onScroll={handleLogScroll}
                    sx={{
                        height: '100%',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        px: {xs: 0.75, sm: 1.5},
                        py: {xs: 0.75, sm: 1.25},
                        pb: unseenCount > 0 ? 7 : {xs: 0.75, sm: 1.25},
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        fontSize: {xs: 12, sm: 13},
                        lineHeight: 1.45,
                        bgcolor: '#0b0f14',
                        overscrollBehavior: 'contain',
                        '&::-webkit-scrollbar': {
                            width: {xs: '4px', sm: '8px'},
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#3d4652',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: '#5a6573',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#0b0f14',
                        },
                    }}
                >
                    {isLoadingLogs && chatMessages.length === 0 ? (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 1}}>
                            <CircularProgress size={22} />
                            <Typography variant="body2">Загружаем последние записи...</Typography>
                        </Box>
                    ) : chatMessages && chatMessages.length > 0 ? (
                        <Box component="ul" sx={{listStyleType: 'none', p: 0, m: 0}}>
                            {chatMessages.map((item, index) => (
                                <Box
                                    component="li"
                                    key={`${item.date || 'log'}-${index}`}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {xs: '56px 1fr', sm: '82px 1fr'},
                                        gap: {xs: 0.75, sm: 1.25},
                                        alignItems: 'baseline',
                                        px: {xs: 0.5, sm: 1},
                                        py: {xs: 0.42, sm: 0.52},
                                        borderRadius: 1,
                                        borderBottom: '1px solid rgba(255,255,255,0.025)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.035)',
                                        },
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: '#6f7b88',
                                            fontFamily: 'inherit',
                                            fontSize: {xs: 10.5, sm: 11.5},
                                            whiteSpace: 'nowrap',
                                            userSelect: 'none',
                                        }}
                                    >
                                        {formatLogDate(item.date)}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: '#d6dde3',
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {item.message}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{py: 6, textAlign: 'center'}}>
                            <Typography color="text.secondary">
                                Сообщений пока нет
                            </Typography>
                        </Box>
                    )}
                </Box>

                {unseenCount > 0 && !isPinnedToBottom && !isPaused && (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<KeyboardArrowDownIcon />}
                        onClick={() => scrollLogsToBottom('smooth')}
                        sx={{
                            position: 'absolute',
                            right: {xs: 12, sm: 20},
                            bottom: {xs: 12, sm: 18},
                            borderRadius: 999,
                            boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                            textTransform: 'none',
                        }}
                    >
                        {unseenCount} новых сообщений
                    </Button>
                )}
            </Box>

            <Paper
                elevation={0}
                square
                sx={{
                    p: {xs: 0.75, sm: 1},
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: '#111820',
                    flexShrink: 0,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="RCON-команда"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && message.trim()) {
                                handleSendMessage();
                            }
                        }}
                        sx={{
                            '& .MuiInputBase-root': {
                                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                fontSize: {xs: 12, sm: 14},
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        endIcon={<SendIcon sx={{display: {xs: 'none', sm: 'inline-flex'}}} />}
                        onClick={handleSendMessage}
                        disabled={!message.trim()}
                        sx={{
                            minWidth: {xs: 88, sm: 132},
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Отправить
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
});

export default Log;
